import { fetchPriceChange, fetchSupplyToEarnReward } from "@/apis/lendingRequests"
import DogCuteFailed from "@/assets/images/dog-cute-failed.png"
import { selectedMainObligationAtom } from "@/atoms/obligations"
import { priceChangeState } from "@/atoms/priceChangeState"
import { networkAtom } from "@/atoms/settings"
import { refreshSetupOnChainByKeyAtom, setupOnChainLoadingStepAtom } from "@/atoms/setupOnChainState"
import { SelectedReserveType } from "@/atoms/shared"
import { supplyToEarnRewardAtom, supplyToEarnVaultRewardBalancesAtom } from "@/atoms/supplyToEarnState"
import {
  BORROW,
  DEPOSIT,
  ERRORS,
  IS_MAINNET,
  RENEC_EXPLORER_URL,
  REPAY,
  RE_NGN,
  RE_VND,
  SOLSCAN_EXPLORER_URL,
  WITHDRAW,
} from "@/utils/constants"
import { HelperText, PopupBorrowRepayState, PopupSupplyWithdrawState, TransactionDetail } from "@/utils/dataTypes"
import { stringifyVariable, waitForTransactionConfirmation } from "@/utils/helpers"
import { RplToken } from "@/utils/modals/RplToken"
import { formatNumberToDigits } from "@/utils/numberFormatter"
import { sendEventLog } from "@/utils/tracking-events"
import { Link, Typography } from "@mui/material"
import { BlockchainNetwork, ConstantsHelper, VND } from "@renec-foundation/relend-adapter"
import { useRollbar } from "@rollbar/react"
import BigNumber from "bignumber.js"
import { useAtomValue, useSetAtom } from "jotai"
import NextLink from "next/link"
import { useQuery } from "react-query"
import { ActionHandlerOutputType } from "./useActionOnchain"
import useTrans from "./useTrans"
import useTransitionSnackbar from "./useTransitionSnackbar"
import useWalletConnect from "./useWalletConnect"

type handleTransactionProps = {
  result: ActionHandlerOutputType
  popupState: PopupSupplyWithdrawState | PopupBorrowRepayState
  amount: number
  selectedReserve?: SelectedReserveType
  walletAddress?: string
  obligationAddress?: string
  actionMax: BigNumber
  buttonError: any
  setTransactionDetail: React.Dispatch<React.SetStateAction<TransactionDetail>>
  setHelperText: React.Dispatch<React.SetStateAction<HelperText>>
}

type handleLiquidateTransactionProps = {
  result: ActionHandlerOutputType
  trans: any
  amount: number
  walletAddress?: string
  setTransactionDetail: React.Dispatch<React.SetStateAction<TransactionDetail>>
  selectedBorrowReserve?: SelectedReserveType
  selectedDepositReserve?: SelectedReserveType
  obligationAddress?: string
  obligationLiquidated?: string
}

type handleClaimToEarnTransactionProps = {
  result: ActionHandlerOutputType
  trans: any
  amount: number
  reserveSymbol: string
  rewardSymbol: string
  walletAddress?: string
  setTransactionDetail: React.Dispatch<React.SetStateAction<TransactionDetail>>
  obligationAddress?: string
  setHelperText: React.Dispatch<React.SetStateAction<HelperText>>
}

type handleClaimNFTVoucherTransactionProps = {
  result: ActionHandlerOutputType
  trans: any
  walletAddress?: string
  setTransactionDetail: React.Dispatch<React.SetStateAction<TransactionDetail>>
  obligationAddress?: string
  setHelperText: React.Dispatch<React.SetStateAction<HelperText>>
}

type handleRefreshSTETransactionProps = {
  result: ActionHandlerOutputType
}

const calculateAmountToDisplay = (amount: number, isCalculatingWithMax: boolean, actionMax: BigNumber): number => {
  if (isCalculatingWithMax) {
    if (amount) {
      const amountBigNum = new BigNumber(amount)
      return amountBigNum.isLessThanOrEqualTo(actionMax) ? amountBigNum.toNumber() : actionMax.toNumber()
    } else {
      return 0
    }
  } else {
    return amount
  }
}

export const useTransactionHandler = () => {
  const trans = useTrans()
  const { address } = useWalletConnect()
  const selectedObligation = useAtomValue(selectedMainObligationAtom)
  const setLoadingStep = useSetAtom(setupOnChainLoadingStepAtom)
  const refreshSetupOnChainByKey = useSetAtom(refreshSetupOnChainByKeyAtom)
  const { refetch: refetchSTE } = useQuery(
    ["fetchSupplyToEarnReward", address, selectedObligation?.address],
    () => fetchSupplyToEarnReward(network, address, selectedObligation?.address),
    {
      staleTime: 10000,
    },
  )
  const network = useAtomValue(networkAtom)
  const rollbar = useRollbar()
  const setPriceChange = useSetAtom(priceChangeState)
  const { handleOpenSnackbar } = useTransitionSnackbar()
  const supplyToEarnRewardBalances = useAtomValue(supplyToEarnVaultRewardBalancesAtom)
  const supplyToEarnRewardRewards = useAtomValue(supplyToEarnRewardAtom)

  const handleTransactionResult = async ({
    result,
    popupState,
    amount,
    selectedReserve,
    walletAddress,
    obligationAddress,
    actionMax,
    buttonError,
    setHelperText,
    setTransactionDetail,
  }: handleTransactionProps) => {
    const isDepositing = popupState.type === DEPOSIT
    const isWithdrawing = popupState.type === WITHDRAW
    const isBorrowing = popupState.type === BORROW
    const isRepaying = popupState.type === REPAY
    const symbol = selectedReserve?.symbol ?? ""
    const tokenDisplayName = selectedReserve
      ? RplToken.getTokenDisplayName(selectedReserve.symbol, selectedReserve.name ?? "")
      : null

    const isBorrowingReVND = (symbol === RE_VND || symbol === VND) && isBorrowing
    const isSolanaNetwork = network === BlockchainNetwork.SOLANA
    const isRenecNetwork = network === BlockchainNetwork.RENEC
    const isWithdrawingWithSTE = isWithdrawing && amount === actionMax.toNumber()

    if (result.isSucceeded) {
      const txHash = result.tx
      const explorerURL = network === BlockchainNetwork.SOLANA ? SOLSCAN_EXPLORER_URL : RENEC_EXPLORER_URL
      const cluster = IS_MAINNET ? "" : network === BlockchainNetwork.SOLANA ? "devnet" : "testnet"
      const link = `${explorerURL}/tx/${txHash}?cluster=${cluster}`
      const amountToDisplay = calculateAmountToDisplay(amount, isWithdrawing || isRepaying, actionMax)
      const modalSucceedTitle = isDepositing
        ? trans.dashboard.modal.supply_successfully
        : isWithdrawing
          ? trans.dashboard.modal.withdraw_successfully
          : isBorrowing
            ? trans.dashboard.modal.borrow_successfully
            : trans.dashboard.modal.repay_successfully
      const modalContent = isDepositing
        ? trans.dashboard.modal.you_just_supplied
        : isWithdrawing
          ? trans.dashboard.modal.you_just_withdrawed
          : isBorrowing
            ? trans.dashboard.modal.you_just_borrowed
            : trans.dashboard.modal.you_just_repaid

      // This is so that reVND can be updated on-chain
      if (txHash) {
        await waitForTransactionConfirmation(network, txHash, 10000)
      }

      if (isBorrowingReVND) {
        setTransactionDetail({
          isError: false,
          isActive: true,
          ctaButtonLink: isSolanaNetwork ? link : "https://nemo.renec.org/sell/reVND",
          title: modalSucceedTitle,
          content: (
            <>
              <Typography mb={1}>
                {modalContent}{" "}
                <Typography component='span' fontWeight={700}>
                  {formatNumberToDigits(amountToDisplay, selectedReserve?.decimals, 0)} {tokenDisplayName}
                </Typography>
              </Typography>
              {isSolanaNetwork ? (
                <Link
                  component={NextLink}
                  href={trans.dashboard.modal.how_to_withdraw_link}
                  target='_blank'
                  className='text-primary'
                >
                  {trans.dashboard.modal.how_to_withdraw}
                </Link>
              ) : null}
            </>
          ),
          ctaButtonTitle: isSolanaNetwork ? trans.dashboard.modal.view_tx : trans.dashboard.modal.withdraw_to_bank,
        })
      } else {
        const steRewardMatch = supplyToEarnRewardRewards.find((reward) => reward.symbol === symbol)
        const steRewardTotalAccumulatedReward = steRewardMatch ? Number(steRewardMatch.totalAccumulatedRewards) : 0
        const steRewardTotalReward = steRewardMatch ? Number(steRewardMatch.totalRewards) : 0

        if (
          steRewardTotalAccumulatedReward === steRewardTotalReward &&
          supplyToEarnRewardBalances[steRewardMatch?.address ?? ""] === 0 &&
          isWithdrawingWithSTE
        ) {
          setTransactionDetail({
            isActive: true,
            isError: true,
            title: trans.dashboard.modal.errors.failed_to_complete,
            resultImage: DogCuteFailed,
            isDisplayingGoHomeButton: false,
            content: trans.dashboard.modal.errors.not_enough_ste_fund,
          })
        } else {
          setTransactionDetail({
            isError: false,
            isActive: true,
            ctaButtonLink: link,
            title: modalSucceedTitle,
            content: (
              <>
                {modalContent}{" "}
                <Typography component='span' fontWeight={700}>
                  {formatNumberToDigits(amountToDisplay, selectedReserve?.decimals, 0)} {tokenDisplayName}
                </Typography>
              </>
            ),
          })
        }
      }

      // google events
      const eventName = isDepositing
        ? "finish_supply"
        : isWithdrawing
          ? "finish_withdraw"
          : isBorrowing
            ? "finish_borrow"
            : "finish_repay"
      sendEventLog(eventName, {
        dapp_name: "renec_lend",
        result: "approved",
        wallet: walletAddress,
        tx: txHash,
        isMainnet: IS_MAINNET,
        network,
      })

      const timeoutRefresh = 500
      setTimeout(() => {
        refreshSetupOnChainByKey(["walletData", "publicKey"])
        setLoadingStep("walletData")
        if ((isWithdrawing || isDepositing) && isRenecNetwork) {
          refetchSTE()
        }
      }, timeoutRefresh)

      // Call fetch price change on successful renec transaction
      if (isRenecNetwork) {
        fetchPriceChange(address, txHash)
          ?.then((priceChange) => {
            if (!!priceChange?.report && Object.keys(priceChange?.report).length > 0) {
              setPriceChange(priceChange?.report)
            }
          })
          .catch((e) => {
            console.error("Failed to fetch price change", e)
          })
      }
    } else {
      const error =
        result.error === ERRORS.ACCOUNT_INITIALIZED && buttonError === ERRORS.INSUFFIENT_BALANCE
          ? buttonError
          : result.error

      const isNonWithdrawalTransaction = isDepositing || isBorrowing || isRepaying

      const ctaButtonLink = ConstantsHelper.isRenecChain(network)
        ? "https://nemo.renec.org/swap/reUSD_RENEC"
        : "https://jup.ag/swap/USDC-SOL"
      const commonTransactionDetails = {
        isActive: true,
        isError: true,
        ctaButtonLink,
        title: trans.dashboard.modal.errors.failed_to_complete,
        ctaButtonTitle: isRenecNetwork
          ? trans.dashboard.modal.errors.deposit_more_renec
          : trans.dashboard.modal.errors.deposit_more_sol,
        isDisplayingGoHomeButton: false,
        resultImage: DogCuteFailed,
      }

      if (error === ERRORS.INSUFFIENT_BALANCE && isNonWithdrawalTransaction && isRenecNetwork) {
        setTransactionDetail({
          ...commonTransactionDetails,
          content: trans.dashboard.modal.errors.please_use_gasless,
        })
      } else if (error?.toString()?.includes(ERRORS.ATTEMPT_BUT_NO_PRIOR_CREDIT)) {
        setTransactionDetail({
          ...commonTransactionDetails,
          content: isRenecNetwork
            ? trans.dashboard.modal.errors.need_renec_for_transaction_fee
            : trans.dashboard.modal.errors.need_sol_for_transaction_fee,
        })
      } else if (error?.toString()?.includes(ERRORS.ERROR_NOT_ENOUGH_FUND) && isWithdrawingWithSTE) {
        setTransactionDetail({
          ...commonTransactionDetails,
          content: trans.dashboard.modal.errors.not_enough_ste_fund,
          ctaButtonTitle: null,
          ctaButtonLink: null,
        })
      }

      setHelperText({ content: error, color: "red" })
      if (
        error?.toString() !== ERRORS.DENY_TRANSACTION &&
        error?.toString() !== ERRORS.REJECT_REQUEST &&
        amount !== 0 &&
        !error?.toString().includes(ERRORS.REFUSE_TRANSACTION)
      ) {
        rollbar.error(
          `${walletAddress}: ${stringifyVariable(error)}. Obligation Address: ${obligationAddress}. Type: ${
            popupState.type
          }. Token: ${symbol}. Amount: ${amount}`,
        )
      }
    }
  }

  const handleLiquidateResult = async ({
    result,
    trans,
    amount,
    walletAddress,
    setTransactionDetail,
    selectedBorrowReserve,
    selectedDepositReserve,
    obligationAddress,
    obligationLiquidated,
  }: handleLiquidateTransactionProps) => {
    const isRenecNetwork = network === BlockchainNetwork.RENEC

    if (result.tx) {
      const explorerURL = network === BlockchainNetwork.SOLANA ? SOLSCAN_EXPLORER_URL : RENEC_EXPLORER_URL
      const cluster = IS_MAINNET ? "" : network === BlockchainNetwork.SOLANA ? "devnet" : "testnet"
      const link = `${explorerURL}/tx/${result.tx}?cluster=${cluster}`
      const tokenDisplayName = selectedBorrowReserve
        ? RplToken.getTokenDisplayName(selectedBorrowReserve.symbol, selectedBorrowReserve.name ?? "")
        : null

      setTransactionDetail({
        isActive: true,
        ctaButtonLink: link,
        title: trans.dashboard.modal.liquidate_successfully,
        content: (
          <>
            {trans.dashboard.modal.you_just_liquidated}{" "}
            <Typography component='span' fontWeight={700}>
              {amount} {tokenDisplayName}
            </Typography>
          </>
        ),
      })
      sendEventLog("liquidate_position", {
        isMainnet: IS_MAINNET,
        collateralReserve: selectedDepositReserve?.symbol,
        needToPayReserve: selectedBorrowReserve?.symbol,
        walletAddress: walletAddress,
        obligationAddress: obligationAddress,
        obligationLiquidated,
        network,
      })
    } else {
      const error = result.error
      const commonTransactionDetails = {
        isActive: true,
        ctaButtonLink: "https://nemo.renec.org/swap/reUSD_RENEC",
        title: trans.dashboard.modal.errors.failed_to_complete,
        ctaButtonTitle: isRenecNetwork
          ? trans.dashboard.modal.errors.deposit_more_renec
          : trans.dashboard.modal.errors.deposit_more_sol,
        resultImage: DogCuteFailed,
        isDisplayingGoHomeButton: false,
      }
      if (error === ERRORS.INSUFFIENT_BALANCE && isRenecNetwork) {
        setTransactionDetail({
          ...commonTransactionDetails,
          content: trans.dashboard.modal.errors.please_use_gasless,
        })
      } else if (error?.toString()?.includes(ERRORS.ATTEMPT_BUT_NO_PRIOR_CREDIT)) {
        setTransactionDetail({
          ...commonTransactionDetails,
          content: isRenecNetwork
            ? trans.dashboard.modal.errors.need_renec_for_transaction_fee
            : trans.dashboard.modal.errors.need_sol_for_transaction_fee,
        })
      } else {
        setTransactionDetail({
          isActive: true,
          title: trans.dashboard.modal.errors.failed_to_complete,
          content: (
            <Typography color='red' textAlign='center'>
              {error}
            </Typography>
          ),
        })
      }
    }
  }

  const handleClaimToEarnResult = async ({
    result,
    trans,
    amount,
    reserveSymbol,
    rewardSymbol,
    walletAddress,
    obligationAddress,
    setTransactionDetail,
    setHelperText,
  }: handleClaimToEarnTransactionProps) => {
    const isRenecNetwork = network === BlockchainNetwork.RENEC

    if (result.isSucceeded) {
      const explorerURL = network === BlockchainNetwork.SOLANA ? SOLSCAN_EXPLORER_URL : RENEC_EXPLORER_URL
      const cluster = IS_MAINNET ? "" : network === BlockchainNetwork.SOLANA ? "devnet" : "testnet"
      const link = `${explorerURL}/tx/${result.tx}?cluster=${cluster}`

      const steRewardMatch = supplyToEarnRewardRewards.find((reward) => reward.symbol === reserveSymbol)
      const steRewardTotalAccumulatedReward = steRewardMatch ? Number(steRewardMatch.totalAccumulatedRewards) : 0
      const steRewardTotalReward = steRewardMatch ? Number(steRewardMatch.totalRewards) : 0

      if (
        steRewardTotalAccumulatedReward === steRewardTotalReward &&
        supplyToEarnRewardBalances[steRewardMatch?.address ?? ""] === 0
      ) {
        setTransactionDetail({
          isActive: true,
          isError: true,
          title: trans.dashboard.modal.errors.failed_to_complete,
          resultImage: DogCuteFailed,
          isDisplayingGoHomeButton: false,
          content: trans.dashboard.modal.errors.not_enough_ste_fund,
        })
      } else {
        setTransactionDetail({
          isActive: true,
          ctaButtonLink: link,
          title: trans.dashboard.modal.claim_successfully,
          // TODO: update claim amount by fetching tx info
          content: trans.dashboard.modal.you_have_claimed_successfully(rewardSymbol),
          // content: (
          //   <>
          //     {trans.dashboard.modal.claim_you_got}{" "}
          //     <Typography component='span' fontWeight={700}>
          //       {amount} {rewardSymbol}
          //     </Typography>
          //   </>
          // ),
        })
      }

      sendEventLog("finish_claim_to_earn", {
        dapp_name: "renec_lend",
        result: "approved",
        wallet: walletAddress,
        tx: result.tx,
        isMainnet: IS_MAINNET,
        network,
      })

      // This is so that reVND can be updated on-chain
      const timeoutRefresh = reserveSymbol === RE_VND || reserveSymbol === RE_NGN || reserveSymbol === VND ? 500 : 100
      setTimeout(() => {
        setLoadingStep("walletData")
        refreshSetupOnChainByKey(["publicKey", "walletData"])
        if (isRenecNetwork) {
          refetchSTE()
        }
      }, timeoutRefresh)
    } else {
      const error = result.error

      const ctaButtonLink = ConstantsHelper.isRenecChain(network)
        ? "https://nemo.renec.org/swap/reUSD_RENEC"
        : "https://jup.ag/swap/USDC-SOL"

      const commonTransactionDetails = {
        isActive: true,
        isError: true,
        ctaButtonLink,
        title: trans.dashboard.modal.errors.failed_to_complete,
        ctaButtonTitle: isRenecNetwork
          ? trans.dashboard.modal.errors.deposit_more_renec
          : trans.dashboard.modal.errors.deposit_more_sol,
        resultImage: DogCuteFailed,
        isDisplayingGoHomeButton: false,
      }

      if (error === ERRORS.INSUFFIENT_BALANCE && isRenecNetwork) {
        setTransactionDetail({
          ...commonTransactionDetails,
          content: trans.dashboard.modal.errors.please_use_gasless,
        })
      } else if (error?.toString()?.includes(ERRORS.ATTEMPT_BUT_NO_PRIOR_CREDIT)) {
        setTransactionDetail({
          ...commonTransactionDetails,
          content: isRenecNetwork
            ? trans.dashboard.modal.errors.need_renec_for_transaction_fee
            : trans.dashboard.modal.errors.need_sol_for_transaction_fee,
        })
      } else if (error?.toString()?.includes(ERRORS.ERROR_NOT_ENOUGH_FUND)) {
        setTransactionDetail({
          ...commonTransactionDetails,
          content: trans.dashboard.modal.errors.not_enough_ste_fund,
          ctaButtonTitle: null,
          ctaButtonLink: null,
        })
      }

      setHelperText({ content: error, color: "red" })
      if (
        error?.toString() !== ERRORS.DENY_TRANSACTION &&
        error?.toString() !== ERRORS.REJECT_REQUEST &&
        amount !== 0 &&
        !error?.toString().includes(ERRORS.REFUSE_TRANSACTION)
      ) {
        rollbar.error(
          `${walletAddress}: ${stringifyVariable(error)}. Obligation Address: ${obligationAddress}. Type: claim_to_earn}. Reserve: ${reserveSymbol}. Reward: ${rewardSymbol}. Amount: ${amount}`,
        )
      }
    }
  }

  const handleClaimNftResult = async ({
    result,
    trans,
    walletAddress,
    obligationAddress,
    setTransactionDetail,
    setHelperText,
  }: handleClaimNFTVoucherTransactionProps) => {
    const isRenecNetwork = network === BlockchainNetwork.RENEC

    if (result.isSucceeded) {
      const explorerURL = network === BlockchainNetwork.SOLANA ? SOLSCAN_EXPLORER_URL : RENEC_EXPLORER_URL
      const cluster = IS_MAINNET ? "" : network === BlockchainNetwork.SOLANA ? "devnet" : "testnet"
      const link = `${explorerURL}/tx/${result.tx}?cluster=${cluster}`

      setTransactionDetail({
        isActive: true,
        ctaButtonLink: link,
        title: trans.dashboard.modal.claim_successfully,
        content: trans.dashboard.modal.claim_nft_voucher_successfully,
      })

      sendEventLog("finish_claim_nft", {
        dapp_name: "renec_lend",
        result: "approved",
        wallet: walletAddress,
        tx: result.tx,
        isMainnet: IS_MAINNET,
        network,
      })
    } else {
      const error = result.error
      const commonTransactionDetails = {
        isActive: true,
        ctaButtonLink: "https://nemo.renec.org/swap/reUSD_RENEC",
        title: trans.dashboard.modal.errors.failed_to_complete,
        ctaButtonTitle: isRenecNetwork
          ? trans.dashboard.modal.errors.deposit_more_renec
          : trans.dashboard.modal.errors.deposit_more_sol,
        resultImage: DogCuteFailed,
        isDisplayingGoHomeButton: false,
      }

      if (error === ERRORS.INSUFFIENT_BALANCE && isRenecNetwork) {
        setTransactionDetail({
          ...commonTransactionDetails,
          content: trans.dashboard.modal.errors.please_use_gasless,
        })
      } else if (error?.toString()?.includes(ERRORS.ATTEMPT_BUT_NO_PRIOR_CREDIT)) {
        setTransactionDetail({
          ...commonTransactionDetails,
          content: isRenecNetwork
            ? trans.dashboard.modal.errors.need_renec_for_transaction_fee
            : trans.dashboard.modal.errors.need_sol_for_transaction_fee,
        })
      }

      setHelperText({ content: error, color: "red" })
      if (
        error?.toString() !== ERRORS.DENY_TRANSACTION &&
        error?.toString() !== ERRORS.REJECT_REQUEST &&
        !error?.toString().includes(ERRORS.REFUSE_TRANSACTION)
      ) {
        rollbar.error(
          `${walletAddress}: ${stringifyVariable(error)}. Obligation Address: ${obligationAddress}. Type: claim NFT Voucher.`,
        )
      }
    }
  }

  const handleRefreshSTERewardResult = async ({ result }: handleRefreshSTETransactionProps) => {
    const isRenecNetwork = network === BlockchainNetwork.RENEC
    if (result.isSucceeded) {
      sendEventLog("finish_refresh_ste_reward", {
        dapp_name: "renec_lend",
        result: "approved",
        wallet: address,
        tx: result.tx,
        isMainnet: IS_MAINNET,
        network,
      })

      setTimeout(() => {
        refetchSTE()
      }, 1000)
      handleOpenSnackbar({
        message: trans.dashboard.table.refresh_ste_reward_successfully,
      })
    } else {
      const error = result.error
      if (error === ERRORS.INSUFFIENT_BALANCE && isRenecNetwork) {
        handleOpenSnackbar({ message: trans.dashboard.modal.errors.please_use_gasless, variant: "error" })
      } else if (error?.toString()?.includes(ERRORS.ATTEMPT_BUT_NO_PRIOR_CREDIT)) {
        handleOpenSnackbar({
          message: isRenecNetwork
            ? trans.dashboard.modal.errors.need_renec_for_transaction_fee
            : trans.dashboard.modal.errors.need_sol_for_transaction_fee,
          variant: "error",
        })
      } else {
        handleOpenSnackbar({
          message: error ?? ERRORS.REJECT_REQUEST,
          variant: "error",
        })
      }

      if (
        error?.toString() !== ERRORS.DENY_TRANSACTION &&
        error?.toString() !== ERRORS.REJECT_REQUEST &&
        !error?.toString().includes(ERRORS.REFUSE_TRANSACTION)
      ) {
        rollbar.error(
          `${address}: ${stringifyVariable(error)}. Obligation Address: ${selectedObligation?.address}. Type: refresh_ste_rewards.`,
        )
      }
    }
  }

  return {
    handleTransactionResult,
    handleLiquidateResult,
    handleClaimToEarnResult,
    handleClaimNftResult,
    handleRefreshSTERewardResult,
  }
}
